import { yupResolver } from '@hookform/resolvers/yup';
import { FC, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { Box, Button, Form, FormField, Input } from '@base-app';
import { CollapsibleSection, ProcessLoader } from '@builder';
import { COMMON_TRANSLATION } from '@common/constants';

import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { GroupIcon, StyledSectionTitle } from './FindPatientId.styles';
import { FindPatientIdFormValues, FindPatientIdProps } from './FindPatientId.types';

const FindPatientId: FC<FindPatientIdProps> = ({ onSubmit, isSearching }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const schema = object({
    patientId: string().required(
      t('form.errors.required', {
        ns: COMMON_TRANSLATION,
        label: t('create.find_patient_id.label'),
      }),
    ),
  });
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<FindPatientIdFormValues>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const renderTitle = (): ReactNode => {
    return (
      <StyledSectionTitle>
        <GroupIcon />
        {t('create.find_patient_id.title')}
      </StyledSectionTitle>
    );
  };

  const handleOnSubmit = async ({ patientId }: FindPatientIdFormValues): Promise<void> => {
    await onSubmit?.(patientId);
  };

  return (
    <CollapsibleSection title={renderTitle()}>
      <Box css={{ padding: '$6', borderRadius: '$4', border: '$borderWidths$1 solid $colors$seafoam500' }}>
        {!isSearching && (
          <Form onSubmit={handleSubmit(handleOnSubmit)}>
            <FormField>
              <Controller
                control={control}
                name="patientId"
                render={({ field }) => (
                  <Input
                    {...field}
                    error={!(errors.patientId == null)}
                    errorDescription={errors.patientId?.message as string}
                    id={field.name}
                    label={t('create.find_patient_id.label', { ns: GENETIC_COUNSELING_TRANSLATION })}
                    name={field.name}
                    placeholder={t('create.find_patient_id.placeholder')}
                    testId="find-patient-id-form-input-patient-id"
                    isSensitive={true}
                  />
                )}
              />
            </FormField>
            <Button id="find-patient-id-form-submit-button" disabled={!isValid} type="submit">
              {t('create.find_patient_id.button')}
            </Button>
          </Form>
        )}
        {isSearching && <ProcessLoader title={t('create.find_patient_id.searching_title')} />}
      </Box>
    </CollapsibleSection>
  );
};

export default FindPatientId;
